import {
  Equal,
  EqualSlash,
  GoatIcon,
  InBetween,
  NotOneOf,
  OneOf,
  Slash,
  Target,
} from '@primer-io/goat-icons';

import { FilterConfig, FilterTypeEnum } from 'SRC/fetch/api/observability';

export const OperatorEnum = {
  SET: 'set',
  NOT_SET: 'notSet',
  /* The `range` operator doesn't map directly to a cubejs
   * operator. When querying, two filters `gte` and `lte`
   * needs to be added.
   * */
  RANGE: 'range',
  EQUALS: 'equals',
  NOT_EQUALS: 'notEquals',
  GTE: 'gte',
  LTE: 'lte',
} as const;
export type OperatorEnum = (typeof OperatorEnum)[keyof typeof OperatorEnum];

export const URLOperatorEnum = {
  IN: 'in',
  NIN: 'nin',
  SET: 'set',
  NSET: 'nset',
  GTE: 'gte',
  LTE: 'lte',
} as const;

const UNARY_OPERATORS = [OperatorEnum.SET, OperatorEnum.NOT_SET] as const;
export const RANGE_OPERATORS = [OperatorEnum.GTE, OperatorEnum.LTE] as const;
export type RangeOperator = (typeof RANGE_OPERATORS)[number];

export const isUnaryOperator = (
  operator?: string,
): operator is (typeof UNARY_OPERATORS)[number] =>
  UNARY_OPERATORS.includes(operator as never);

export type OperatorConfig = {
  value: OperatorEnum;
  label: string;
  icon: GoatIcon;
};
const STRING_OPERATORS = [
  {
    value: OperatorEnum.EQUALS,
    label: 'one of',
    icon: OneOf,
  },
  {
    value: OperatorEnum.NOT_EQUALS,
    label: 'not one of',
    icon: NotOneOf,
  },
  {
    value: OperatorEnum.NOT_SET,
    label: 'is null',
    icon: Slash,
  },
  {
    value: OperatorEnum.SET,
    label: 'is not null',
    icon: Target,
  },
] as const satisfies OperatorConfig[];
export type StringOperator = (typeof STRING_OPERATORS)[number]['value'];

const NUMBER_OPERATORS = [
  {
    value: OperatorEnum.RANGE,
    label: 'between',
    icon: InBetween,
  },
  {
    value: OperatorEnum.EQUALS,
    label: 'equals',
    icon: Equal,
  },
  {
    value: OperatorEnum.NOT_EQUALS,
    label: 'not equals',
    icon: EqualSlash,
  },
] as const satisfies OperatorConfig[];

export const getOperatorOptions = (type: FilterConfig['type']) =>
  type === FilterTypeEnum.NUMBER || type === FilterTypeEnum.CURRENCY
    ? NUMBER_OPERATORS
    : STRING_OPERATORS;

export const OPERATORS_URL_KEYS = [
  URLOperatorEnum.SET,
  URLOperatorEnum.GTE,
  URLOperatorEnum.LTE,
  URLOperatorEnum.IN,
  URLOperatorEnum.NIN,
  URLOperatorEnum.NSET,
] as const;
export type UrlOperator = (typeof OPERATORS_URL_KEYS)[number];

export function isURLOperator(operator: string): operator is UrlOperator {
  return OPERATORS_URL_KEYS.includes(operator as never);
}

export const toUrlOperator = (operator: OperatorEnum): UrlOperator =>
  (
    ({
      [OperatorEnum.SET]: URLOperatorEnum.SET,
      [OperatorEnum.GTE]: URLOperatorEnum.GTE,
      [OperatorEnum.LTE]: URLOperatorEnum.LTE,
      [OperatorEnum.EQUALS]: URLOperatorEnum.IN,
      [OperatorEnum.NOT_EQUALS]: URLOperatorEnum.NIN,
      [OperatorEnum.NOT_SET]: URLOperatorEnum.NSET,
    }) as const
  )[operator];

export const toOperator = (urlOperator: UrlOperator): OperatorEnum =>
  (
    ({
      [URLOperatorEnum.SET]: OperatorEnum.SET,
      [URLOperatorEnum.GTE]: OperatorEnum.GTE,
      [URLOperatorEnum.LTE]: OperatorEnum.LTE,
      [URLOperatorEnum.IN]: OperatorEnum.EQUALS,
      [URLOperatorEnum.NIN]: OperatorEnum.NOT_EQUALS,
      [URLOperatorEnum.NSET]: OperatorEnum.NOT_SET,
    }) as const
  )[urlOperator]!;
